import { v4 as uuidv4 } from 'uuid';
import config from '../config/api';
import logger from '../logging/logger';

export interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  updated_at?: string;
  last_activity: string;
  message_count?: number;
  status: string;
}

export type MessageFeedback = 'thumbs_up' | 'thumbs_down';

export interface Message {
  id: string;
  persistedMessageId?: string | null;
  content: string;                 // always string for ChatWindow + streaming safety
  contentObj?: any;                // structured payload (charts/data/strategy_config/etc)
  role: 'user' | 'assistant';
  timestamp: string;
  sender?: string;
  feedback?: MessageFeedback | null;
  queuedFeedback?: MessageFeedback | null;
  hasQueuedFeedback?: boolean;
  feedbackSyncState?: 'synced' | 'pending' | 'failed';
}

export class SessionService {
  private baseUrl = config.apiUrl;

  // Session Management Methods
  async createSession(userUuid: string, token: string, title?: string): Promise<ChatSession> {
    const sessionId = uuidv4();

    const response = await fetch(`${this.baseUrl}/create_session?user_id=${userUuid}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: sessionId,
        title: title || 'New Chat',
        model: 'gpt-4',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create session');
    }

    return response.json();
  }

  async getSessions(
    userUuid: string,
    token: string,
    limit: number = 30,
    offset: number = 0
  ): Promise<{ sessions: ChatSession[]; hasMore: boolean; total: number }> {
    const response = await fetch(`${this.baseUrl}/sessions?user_id=${userUuid}&limit=${limit}&offset=${offset}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sessions');
    }

    const data = await response.json();
    return {
      sessions: data.sessions || [],
      hasMore: data.has_next || false,
      total: data.total || 0,
    };
  }

  async getSession(sessionId: string, userUuid: string, token: string): Promise<ChatSession> {
    const response = await fetch(`${this.baseUrl}/session/${sessionId}?user_id=${userUuid}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch session for user');
    }

    return response.json();
  }

  async renameSession(sessionId: string, newTitle: string, userUuid: string, token: string): Promise<ChatSession> {
    const response = await fetch(`${this.baseUrl}/session/${sessionId}?user_id=${userUuid}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title: newTitle }),
    });

    if (!response.ok) {
      throw new Error('Failed to rename session');
    }

    return response.json();
  }

  async deleteSession(sessionId: string, userUuid: string, token: string): Promise<void> {
    // Calls session_data endpoint which deletes both PostgreSQL session and all MongoDB data
    const response = await fetch(`${this.baseUrl}/session_data/${sessionId}?user_id=${userUuid}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    let responseBody: any = null;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = null;
    }

    if (!response.ok) {
      const details = Array.isArray(responseBody?.errors) && responseBody.errors.length > 0
        ? `: ${responseBody.errors.join('; ')}`
        : '';
      throw new Error(`Failed to delete session${details}`);
    }

    if (responseBody?.success === false) {
      const details = Array.isArray(responseBody?.errors) && responseBody.errors.length > 0
        ? responseBody.errors.join('; ')
        : 'unknown cleanup error';
      throw new Error(`Failed to delete session: ${details}`);
    }
  }

  async archiveSession(sessionId: string, userUuid: string, token: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/session/${sessionId}/archive?user_id=${userUuid}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to archive session');
    }
  }

  // Message Operations
  async getMessages(
    sessionId: string,
    userUuid: string,
    token: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<{ messages: Message[]; hasMore: boolean; total: number }> {
    const response = await fetch(
      `${this.baseUrl}/session/${sessionId}/messages?user_id=${userUuid}&page=${page}&page_size=${pageSize}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch messages');
    }

    const data = await response.json();

    const normalize = (c: any): { text: string; obj?: any } => {
      if (typeof c === 'string') return { text: c };
      if (c && typeof c === 'object') return { text: c.text ?? '', obj: c };
      return { text: String(c ?? '') };
    };

    const messages = (data.messages ?? []).map((msg: any) => {
      const { text, obj } = normalize(msg.content);

      // Debug logging for backtest_result messages
      if (obj?.backtest_result) {
        logger.info('📥 SessionService: Normalized message with backtest_result:', {
          message_id: msg.message_id || msg.id,
          backtest_result: obj.backtest_result,
          raw_content: msg.content,
        });
      }

      return {
        id: msg.message_id || msg.id,
        persistedMessageId: msg.message_id || msg.id,
        content: text,
        contentObj: obj,
        role: msg.role,
        timestamp: msg.created_at || msg.timestamp,
        sender: msg.role,
        feedback: msg.feedback ?? null,
        hasQueuedFeedback: false,
        feedbackSyncState: 'synced',
      };
    });

    return {
      messages,
      hasMore: data.has_next || false,
      total: data.total || 0,
    };
  }

  async generateSessionTitleUsingAIAgent(
    sessionId: string,
    userUuid: string,
    token: string,
    onTitleUpdate?: (title: string) => void
  ): Promise<{ title: string }> {
    return new Promise((resolve, reject) => {
      const eventSource = new EventSource(
        `${this.baseUrl}/session/${sessionId}/generate-title-stream?user_id=${userUuid}&token=${encodeURIComponent(token)}`
      );

      let finalTitle = 'New Chat';
      let lastUpdateTime = 0;
      const throttleDelay = 100;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'title_start') {
            // Title generation is starting - can show loading indicator if needed
            logger.info('📝 Title generation started');
          } else if (data.type === 'title_chunk') {
            finalTitle = data.title;

            const now = Date.now();
            if (onTitleUpdate && now - lastUpdateTime > throttleDelay) {
              onTitleUpdate(finalTitle);
              lastUpdateTime = now;
            }
          } else if (data.type === 'title_complete') {
            finalTitle = data.title;
            if (onTitleUpdate) onTitleUpdate(finalTitle);
            eventSource.close();
            resolve({ title: finalTitle });
          } else if (data.type === 'error') {
            // Still resolve with the title (even if it's "New Chat") rather than rejecting
            finalTitle = data.title || 'New Chat';
            if (onTitleUpdate) onTitleUpdate(finalTitle);
            eventSource.close();
            resolve({ title: finalTitle });
          }
        } catch (error) {
          logger.error('Error parsing SSE data:', error);
        }
      };

      eventSource.onerror = (error) => {
        logger.error('SSE connection error:', error);
        eventSource.close();
        reject(new Error('Connection error during title generation'));
      };

      setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          eventSource.close();
          resolve({ title: finalTitle });
        }
      }, 30000);
    });
  }

  async updateMessageFeedback(
    sessionId: string,
    messageId: string,
    userUuid: string,
    token: string,
    feedback: MessageFeedback | null
  ): Promise<boolean> {
    const response = await fetch(
      `${this.baseUrl}/session/${sessionId}/messages/${messageId}/feedback?user_id=${userUuid}`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feedback }),
      }
    );

    if (!response.ok) {
      let responseBody: string | null = null;
      try {
        responseBody = await response.text();
      } catch {
        responseBody = null;
      }

      const error = new Error(`Failed to update message feedback (HTTP ${response.status})`) as Error & {
        status?: number;
        responseBody?: string | null;
      };
      error.status = response.status;
      error.responseBody = responseBody;
      throw error;
    }

    const data = await response.json();
    return Boolean(data?.updated);
  }

  /**
   * Generate title from user message directly.
   * Returns { title: '', skipped: true } if message is a greeting.
   */
  async generateTitleFromMessage(
    sessionId: string,
    userUuid: string,
    token: string,
    message: string,
    onTitleUpdate?: (title: string) => void
  ): Promise<{ title: string; skipped: boolean }> {
    return new Promise((resolve, _reject) => {
      // Use fetch with POST for sending the message
      fetch(`${this.baseUrl}/session/${sessionId}/generate-title-from-message?user_id=${userUuid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      }).then(response => {
        if (!response.ok) {
          throw new Error('Failed to generate title from message');
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        let finalTitle = '';
        let skipped = false;
        let lastUpdateTime = 0;
        const throttleDelay = 100;
        const decoder = new TextDecoder();
        let buffer = '';

        const processChunk = (chunk: string) => {
          buffer += chunk;
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (data.type === 'title_start') {
                  logger.info('📝 Title from message generation started');
                } else if (data.type === 'title_skip') {
                  logger.info('📝 Title generation skipped - greeting detected');
                  skipped = true;
                  reader.cancel();
                  resolve({ title: '', skipped: true });
                  return true; // Signal to stop processing
                } else if (data.type === 'title_chunk') {
                  finalTitle = data.title;
                  const now = Date.now();
                  if (onTitleUpdate && now - lastUpdateTime > throttleDelay) {
                    onTitleUpdate(finalTitle);
                    lastUpdateTime = now;
                  }
                } else if (data.type === 'title_complete') {
                  finalTitle = data.title;
                  if (onTitleUpdate) onTitleUpdate(finalTitle);
                  reader.cancel();
                  resolve({ title: finalTitle, skipped: false });
                  return true;
                } else if (data.type === 'error') {
                  logger.error('Title generation error:', data.message);
                  reader.cancel();
                  resolve({ title: '', skipped: true });
                  return true;
                }
              } catch (e) {
                logger.error('Error parsing SSE data:', e);
              }
            }
          }
          return false;
        };

        const readStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                // Process any remaining buffer
                if (buffer) {
                  processChunk('\n');
                }
                if (!skipped && !finalTitle) {
                  resolve({ title: '', skipped: true });
                }
                break;
              }
              const shouldStop = processChunk(decoder.decode(value, { stream: true }));
              if (shouldStop) break;
            }
          } catch (e) {
            logger.error('Error reading stream:', e);
            resolve({ title: '', skipped: true });
          }
        };

        readStream();
      }).catch(error => {
        logger.error('Failed to generate title from message:', error);
        resolve({ title: '', skipped: true });
      });

      // Timeout after 15 seconds
      setTimeout(() => {
        resolve({ title: '', skipped: true });
      }, 15000);
    });
  }

  /**
   * Generate title from session message history.
   */
  async generateTitleFromHistory(
    sessionId: string,
    userUuid: string,
    token: string,
    onTitleUpdate?: (title: string) => void
  ): Promise<{ title: string }> {
    return new Promise((resolve, reject) => {
      const eventSource = new EventSource(
        `${this.baseUrl}/session/${sessionId}/generate-title-from-history?user_id=${userUuid}&token=${encodeURIComponent(token)}`
      );

      let finalTitle = 'New Chat';
      let lastUpdateTime = 0;
      const throttleDelay = 100;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'title_start') {
            logger.info('📝 Title from history generation started');
          } else if (data.type === 'title_chunk') {
            finalTitle = data.title;

            const now = Date.now();
            if (onTitleUpdate && now - lastUpdateTime > throttleDelay) {
              onTitleUpdate(finalTitle);
              lastUpdateTime = now;
            }
          } else if (data.type === 'title_complete') {
            finalTitle = data.title;
            if (onTitleUpdate) onTitleUpdate(finalTitle);
            eventSource.close();
            resolve({ title: finalTitle });
          } else if (data.type === 'error') {
            finalTitle = data.title || 'New Chat';
            if (onTitleUpdate) onTitleUpdate(finalTitle);
            eventSource.close();
            resolve({ title: finalTitle });
          }
        } catch (error) {
          logger.error('Error parsing SSE data:', error);
        }
      };

      eventSource.onerror = (error) => {
        logger.error('SSE connection error:', error);
        eventSource.close();
        reject(new Error('Connection error during title generation'));
      };

      setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          eventSource.close();
          resolve({ title: finalTitle });
        }
      }, 30000);
    });
  }
}
